/**
 * AdhocFeeSection — Admin surface (Fees page) to charge a one-time ad-hoc fee directly to an
 * Agreement (#2), with no Account or fee schedule. Create (amount ex-VAT + VAT rate +
 * description + billing month) and cancel; VAT + total are derived server-side. The fee
 * surfaces in View History and the accountant export for that month (export-fed — the
 * accountant raises the invoice; Pedro 2026-09-13).
 */
import { Button, Card, Group, NumberInput, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { AgreementPicker } from './EntityPicker';
import { adhocFeesApi } from '../api/adhocFees';
import { eurToCents, fmtEurFromCents, pctToBps } from '../api/feeSchedules';
import type { UUID } from '../api/types';

export function AdhocFeeSection() {
  const qc = useQueryClient();
  const [agreementId, setAgreementId] = useState<UUID | null>(null);
  const [month, setMonth] = useState('');                 // 'YYYY-MM-01'
  const [description, setDescription] = useState('');
  const [amountEur, setAmountEur] = useState<number | ''>('');
  const [vatPct, setVatPct] = useState<number | ''>(23);
  const [saving, setSaving] = useState(false);

  const listKey = ['adhoc-fees', 'agreement', agreementId] as const;
  const listQ = useQuery({
    queryKey: listKey,
    queryFn: () => adhocFeesApi.listForAgreement(agreementId as UUID),
    enabled: !!agreementId,
  });

  const canSave = !!agreementId && !!month && description.trim().length > 0
    && amountEur !== '' && vatPct !== '';

  async function handleCreate() {
    if (!canSave) return;
    setSaving(true);
    try {
      await adhocFeesApi.create({
        agreement_id: agreementId as UUID,
        period_month: month,
        description: description.trim(),
        net_fee_cents: eurToCents(Number(amountEur)),
        vat_rate_bps: pctToBps(Number(vatPct)),
      });
      notifications.show({ color: 'green', message: 'Ad-hoc fee created' });
      setDescription(''); setAmountEur('');
      qc.invalidateQueries({ queryKey: listKey });
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Could not create the ad-hoc fee',
        message: e instanceof Error ? e.message : 'Failed.',
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleCancel(id: UUID) {
    try {
      await adhocFeesApi.cancel(id);
      notifications.show({ color: 'gray', message: 'Ad-hoc fee cancelled' });
      qc.invalidateQueries({ queryKey: listKey });
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Could not cancel',
        message: e instanceof Error ? e.message : 'Failed.',
      });
    }
  }

  const fees = listQ.data ?? [];

  return (
    <Stack>
      {/* overflow visible: the Agreement picker's list may hang below the card (C-385). */}
      <Card withBorder padding="lg" radius="md" style={{ overflow: 'visible' }}>
        <Stack gap="sm">
          <Title order={4}>Charge a one-time fee</Title>
          <Text size="sm" c="dimmed">
            A single, ad-hoc fee billed directly to an Agreement — no Account or fee schedule
            needed. It appears in View History and the accountant export for the billing month
            (the accountant raises the invoice from the export). VAT is added at the rate you set.
            A negative amount records a one-off credit.
          </Text>
          <AgreementPicker label="Agreement" value={agreementId} onChange={setAgreementId} />
          <Group grow align="flex-start">
            <MonthPickerInput
              label="Billing month" placeholder="Pick a month"
              value={month ? new Date(`${month}T00:00:00`) : null}
              onChange={(d) => setMonth(
                d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` : '',
              )}
            />
            <NumberInput
              label="Amount (ex VAT, EUR)" placeholder="0.00"
              value={amountEur} decimalScale={2} step={0.01}
              onChange={(v) => setAmountEur(v === '' || v === null ? '' : Number(v))}
            />
            <NumberInput
              label="VAT rate (%)" value={vatPct} decimalScale={2} step={0.5} min={0}
              onChange={(v) => setVatPct(v === '' || v === null ? '' : Number(v))}
            />
          </Group>
          <TextInput
            label="Description"
            placeholder="e.g. One-time advisory setup fee"
            description="Appears as the invoice narrative (line 1) in the accountant export."
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button onClick={handleCreate} loading={saving} disabled={!canSave}>
              Create ad-hoc fee
            </Button>
          </Group>
        </Stack>
      </Card>

      {agreementId && (
        <Card withBorder padding="lg" radius="md">
          <Stack gap="sm">
            <Title order={5}>Ad-hoc fees on this agreement</Title>
            {listQ.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
            {!listQ.isLoading && fees.length === 0 && (
              <Text size="sm" c="dimmed">No ad-hoc fees on this agreement yet.</Text>
            )}
            {fees.length > 0 && (
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Month</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Ex VAT</Table.Th>
                    <Table.Th ta="right">VAT</Table.Th>
                    <Table.Th ta="right">Total</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {fees.map((f) => (
                    <Table.Tr key={f.id}>
                      <Table.Td>{f.period_month.slice(0, 7)}</Table.Td>
                      <Table.Td>{f.description}</Table.Td>
                      <Table.Td ta="right">{fmtEurFromCents(f.net_fee_cents)}</Table.Td>
                      <Table.Td ta="right">{fmtEurFromCents(f.vat_cents)}</Table.Td>
                      <Table.Td ta="right">{fmtEurFromCents(f.total_fee_cents)}</Table.Td>
                      <Table.Td ta="right">
                        <Button size="xs" variant="subtle" color="red"
                          onClick={() => handleCancel(f.id)}>
                          Cancel
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
